import { FileText, PenTool, BookOpen, Calendar, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { UrgencyBadge, getUrgencyConfig } from '@/components/shared/UrgencyBadge';
import { FrameworkBadge } from '@/components/shared/FrameworkBadge';
import type { SSEEvent } from '@/types/api';
import { cn } from '@/lib/utils';

interface FeedEventCardProps {
  readonly event: SSEEvent;
  readonly isHistorical?: boolean;
  readonly onClick?: () => void;
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1_000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function formatDocumentType(raw: string): string {
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatEffectiveDate(date: string | null): string | null {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function generateSummary(event: SSEEvent): string | null {
  if (event.type !== 'change_event_processed') return null;
  const { documentType, frameworksAffected, affectedCardCount, sourceName } = event.payload;
  const docLabel = formatDocumentType(documentType).toLowerCase();
  const fwList = Array.isArray(frameworksAffected) && frameworksAffected.length > 0
    ? frameworksAffected.slice(0, 3).join(', ')
    : 'regulatory';
  return `New ${docLabel} from ${sourceName} affecting ${fwList} compliance. ${affectedCardCount} document${affectedCardCount !== 1 ? 's' : ''} may require updates.`;
}

function getDeadlineChip(effectiveDate: string | null): { label: string; className: string } | null {
  if (!effectiveDate) return null;
  const now = Date.now();
  const target = new Date(effectiveDate).getTime();
  const daysLeft = Math.ceil((target - now) / 86_400_000);
  if (daysLeft < 0) return { label: `${Math.abs(daysLeft)}d overdue`, className: 'bg-compliance-critical/15 text-compliance-critical' };
  if (daysLeft <= 14) return { label: `${daysLeft}d left`, className: 'bg-compliance-critical/15 text-compliance-critical' };
  if (daysLeft <= 30) return { label: `${daysLeft}d left`, className: 'bg-compliance-high/15 text-compliance-high' };
  if (daysLeft <= 90) return { label: `${daysLeft}d left`, className: 'bg-compliance-medium/15 text-compliance-medium' };
  return null;
}

const STATUS_CHIPS: Record<string, { label: string; className: string }> = {
  pending_review: { label: 'Pending Review', className: 'bg-teal/10 text-teal' },
  review_pending: { label: 'Pending Review', className: 'bg-teal/10 text-teal' },
  approved: { label: 'Approved', className: 'bg-compliance-low/15 text-compliance-low' },
  published: { label: 'Published', className: 'bg-compliance-low/15 text-compliance-low' },
  escalated: { label: 'Escalated', className: 'bg-compliance-critical/15 text-compliance-critical' },
  draft: { label: 'Draft', className: 'bg-muted text-muted-foreground' },
};

const EVENT_ICONS = {
  change_event_processed: FileText,
  draft_generated: PenTool,
  document_published: BookOpen,
} as const;

const EVENT_LABELS = {
  change_event_processed: 'Change Event',
  draft_generated: 'Draft Generated',
  document_published: 'Published',
} as const;

export function FeedEventCard({ event, isHistorical, onClick }: FeedEventCardProps) {
  const Icon = EVENT_ICONS[event.type];
  const label = EVENT_LABELS[event.type];

  // Urgency-keyed border color
  const rawScore = event.type === 'change_event_processed'
    ? event.payload.urgencyScore
    : event.type === 'draft_generated'
      ? event.payload.urgencyScore
      : 0;
  const urgencyScore = Number(rawScore) || 0;
  const urgencyConfig = getUrgencyConfig(urgencyScore);

  return (
    <Card
      className={cn(
        'border-l-2 cursor-pointer transition-colors hover:bg-muted/40',
        urgencyConfig.borderColor,
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>

          <div className="min-w-0 flex-1">
            {/* Header row: label + badges + timestamp */}
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {label}
                <span
                  className={cn(
                    'rounded px-1 py-0.5 text-[10px] font-semibold leading-none',
                    isHistorical
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-teal/10 text-teal',
                  )}
                >
                  {isHistorical ? 'Historical' : 'Live'}
                </span>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatRelativeTime(event.timestamp)}
              </span>
            </div>

            {event.type === 'change_event_processed' && (
              <>
                {/* Title — human-readable, not just source ID */}
                <p className="mt-1 text-sm font-medium leading-snug text-foreground">
                  {formatDocumentType(event.payload.documentType)}: {event.payload.sourceName}
                </p>

                {/* Summary */}
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground line-clamp-2">
                  {generateSummary(event)}
                </p>

                {/* Urgency + Document type + Effective date + Deadline + Status */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <UrgencyBadge score={Number(event.payload.urgencyScore) || 0} />
                  <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {formatDocumentType(event.payload.documentType)}
                  </span>
                  {event.payload.effectiveDate ? (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      Effective: {formatEffectiveDate(event.payload.effectiveDate)}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground/60">
                      No deadline set
                    </span>
                  )}
                  {(() => {
                    const chip = getDeadlineChip(event.payload.effectiveDate);
                    if (!chip) return null;
                    return (
                      <span className={cn('flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', chip.className)}>
                        <Clock className="h-2.5 w-2.5" />
                        {chip.label}
                      </span>
                    );
                  })()}
                  {STATUS_CHIPS[event.payload.status] && (
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_CHIPS[event.payload.status].className)}>
                      {STATUS_CHIPS[event.payload.status].label}
                    </span>
                  )}
                </div>

                {/* Frameworks */}
                {Array.isArray(event.payload.frameworksAffected) && event.payload.frameworksAffected.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    {event.payload.frameworksAffected.map((fw) => (
                      <FrameworkBadge key={fw} framework={fw} />
                    ))}
                  </div>
                )}

                {/* Affected departments */}
                {event.payload.affectedDepartments.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    {event.payload.affectedDepartments.map((dept) => (
                      <span
                        key={dept}
                        className="rounded bg-navy-light/60 px-1.5 py-0.5 text-[10px] font-medium text-foreground/80"
                      >
                        {formatDocumentType(dept)}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {event.type === 'draft_generated' && (
              <>
                <p className="mt-1 text-sm font-medium text-foreground">
                  New draft for {formatDocumentType(event.payload.department)}
                </p>
                <div className="mt-2 flex items-center gap-1.5">
                  <UrgencyBadge score={Number(event.payload.urgencyScore) || 0} />
                  <span className="text-xs text-muted-foreground">
                    Card {event.payload.cardId.slice(0, 8)}
                  </span>
                </div>
              </>
            )}

            {event.type === 'document_published' && (
              <>
                <p className="mt-1 text-sm font-medium text-foreground">
                  {event.payload.documentTitle}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {formatDocumentType(event.payload.department)} — published{' '}
                  {formatRelativeTime(event.payload.publishedAt)}
                </p>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
